import { ChangeDetectorRef, Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { CommonNoteViewModel, EsraPipingAPIClient, PipeClassViewModel } from 'src/app/shared/models/autogenerated-piping';
import { first } from 'rxjs/operators';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';

/**
 * A single row in the compare-mode diff view.
 * Notes are matched by `codeId`; both `codeId` and `note` text are compared to
 * determine the diff status.
 */
interface NoteDiffRow {
  /** The note code ID — from the current revision when added/unchanged/changed, from the
   *  previous revision when removed. */
  codeId: string;
  /** Note text in the current revision, or `null` when the note was deleted. */
  currentNote: string | null;
  /** Note text in the previous revision, or `null` when the note was added. */
  previousNote: string | null;
  /** Diff classification for this row. */
  status: 'unchanged' | 'changed' | 'added' | 'removed';
}

@Component({
    selector: 'app-piping-notes',
    templateUrl: './piping-notes.component.html',
    styleUrls: ['./piping-notes.component.scss'],
    standalone: false
})
export class PipingNotesComponent implements OnInit, OnChanges {
  @Input() pipeClassViewModel: PipeClassViewModel;
  @Input() version?: number; // optional bump from parent to force refresh
  @Input() isCompareMode: boolean = false;
  /** The previous revision's pipe class view model; required for compare mode. */
  @Input() previousPipeClassViewModel: PipeClassViewModel | null = null;

  displayedColumns: string[] = ["ID", "Note"];
  searchInputString = "";
  faSearch = faSearch;
  commonNoteViewModels: CommonNoteViewModel[] = [];
  dataSource: CommonNoteViewModel[] = [];
  currentPage = 0;
  pageSize = 10;
  totalCount: number;
  pageSizes = [2, 5, 10, 25, 50, 100];
  currentSorting: Sort = { active: 'ID', direction: 'asc' };
  @ViewChild('paginator') paginator: MatPaginator;

  /** Precomputed diff rows for compare mode. Sorted by codeId numerically. */
  noteDiffs: NoteDiffRow[] = [];
  /** Notes loaded from the previous revision, used only in compare mode. */
  private previousCommonNotes: CommonNoteViewModel[] = [];
  /**
   * Guards `loadPreviousNotes` so it only fires once per compare session.
   * Reset when compare mode is turned off so re-entering compare re-fetches if needed.
   */
  private previousNotesLoaded: boolean = false;

  constructor(
    private esraPipingAPIClient: EsraPipingAPIClient,
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Always set default sorting to ID ascending (menor a mayor)
    this.currentSorting = { active: 'ID', direction: 'asc' };

    // If commonNotes already populated (runtime aggregation), use them directly
    if (this.pipeClassViewModel?.commonNotes && this.pipeClassViewModel.commonNotes.length > 0) {
      this.loadCommonNotes(this.pipeClassViewModel.commonNotes as CommonNoteViewModel[]);
    } else {
      const pipeClassId = this.pipeClassViewModel?.id;
      if (pipeClassId) {
        this.esraPipingAPIClient.getAllCommonNotesForPipeClassByPipeClassId(pipeClassId)
          .pipe(first()).subscribe(response => {
            this.loadCommonNotes(response);
          });
      }
    }
    // Compare mode is handled exclusively by ngOnChanges (which fires before ngOnInit on first
    // render and on every subsequent input change). loadPreviousNotes must not be called here
    // to avoid a double-fetch on the first tab activation with compare already active.
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Load previous notes FIRST so that previousCommonNotes is populated before
    // computeNoteDiff is called inside loadCommonNotes below.
    // Both isCompareMode and previousPipeClassViewModel can arrive in the same ngOnChanges
    // call (e.g. when the parent toggles compare mode); the flag prevents double-loading.
    if (changes['previousPipeClassViewModel'] || changes['isCompareMode']) {
      if (this.isCompareMode && this.previousPipeClassViewModel) {
        if (changes['previousPipeClassViewModel'] || !this.previousNotesLoaded) {
          // New previous pipe class target, or first compare entry — load notes.
          // Clear the cache so loadPreviousNotes starts fresh.
          this.previousCommonNotes = [];
          this.previousNotesLoaded = false;
          this.loadPreviousNotes();
        } else {
          // Re-entering compare mode with the same previous pipe class — notes are already
          // cached in previousCommonNotes. Recompute diff in case current notes changed
          // while compare was inactive, then mark for check so OnPush picks it up.
          this.computeNoteDiff();
          this.cdr.markForCheck();
        }
      } else if (!this.isCompareMode) {
        // Clear the diff display when exiting compare.
        // Keep previousCommonNotes cached so toggling back on is instant — no API call.
        this.noteDiffs = [];
      }
    }

    if ((changes['pipeClassViewModel'] || changes['version']) && this.pipeClassViewModel?.commonNotes) {
      // Always ensure sorting is applied when data changes
      this.currentSorting = { active: 'ID', direction: 'asc' };
      this.loadCommonNotes(this.pipeClassViewModel.commonNotes as CommonNoteViewModel[]);
    }
  }

  private loadCommonNotes(response: CommonNoteViewModel[]) {
    this.commonNoteViewModels = response;

    // Always sort by ID ascending (menor a mayor) before displaying
    this.currentSorting = { active: 'ID', direction: 'asc' };

    const sortedResponse = this.sortResults([...response]);

    this.dataSource = sortedResponse;
    this.totalCount = response.length;
    this.updatePage({ pageIndex: 0, pageSize: this.pageSize, length: this.totalCount });

    // Recompute diff whenever current notes reload in compare mode.
    // Call markForCheck() because this method can be invoked from an async API subscription,
    // and the parent component uses OnPush change detection — the view won't update otherwise.
    if (this.isCompareMode) {
      this.computeNoteDiff();
      this.cdr.markForCheck();
    }
  }

  /**
   * Loads common notes for the previous pipe class view model.
   * Reads `previousPipeClassViewModel.commonNotes` if already populated by the API response.
   * Falls back to `getAllCommonNotesForPipeClassByPipeClassId` when `commonNotes` is absent —
   * the same pattern used in `ngOnInit` for the current pipe class, because the
   * `getPipeClassByPipeClassId` endpoint does not always include `commonNotes` inline.
   *
   * Only called once per compare session — guarded by `previousNotesLoaded`.
   * Calls `markForCheck()` after every path so the diff renders immediately even when
   * called from an asynchronous context (parent uses OnPush change detection).
   */
  private loadPreviousNotes(): void {
    this.previousNotesLoaded = true;
    const prev: PipeClassViewModel | null = this.previousPipeClassViewModel;
    if (!prev) { return; }

    if (prev.commonNotes && prev.commonNotes.length > 0) {
      // commonNotes already populated in the API response — use directly.
      this.previousCommonNotes = prev.commonNotes as CommonNoteViewModel[];
      this.computeNoteDiff();
      this.cdr.markForCheck();
    } else if (prev.id) {
      // commonNotes absent from the API response — fetch separately.
      // This mirrors the ngOnInit fallback used for the current pipe class.
      this.esraPipingAPIClient.getAllCommonNotesForPipeClassByPipeClassId(prev.id)
        .pipe(first()).subscribe(notes => {
          this.previousCommonNotes = notes;
          this.computeNoteDiff();
          this.cdr.markForCheck();
        });
    }
  }

  /**
   * Builds `noteDiffs` by matching notes from the current and previous revisions by `codeId`,
   * then comparing both the id and the note text to classify each as unchanged, changed,
   * added, or removed. The result is sorted numerically by `codeId`.
   */
  private computeNoteDiff(): void {
    const currentMap = new Map<string, string>();
    const previousMap = new Map<string, string>();

    (this.commonNoteViewModels || []).forEach(n => currentMap.set(n.codeId, n.note));
    (this.previousCommonNotes || []).forEach(n => previousMap.set(n.codeId, n.note));

    const allIds: Set<string> = new Set([...currentMap.keys(), ...previousMap.keys()]);
    const sortedIds: string[] = [...allIds].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

    this.noteDiffs = sortedIds.map(codeId => {
      const current: string | null = currentMap.get(codeId) ?? null;
      const previous: string | null = previousMap.get(codeId) ?? null;

      let status: NoteDiffRow['status'];
      if (current !== null && previous !== null) {
        // Both ID and text must match for a note to be considered unchanged
        status = current === previous ? 'unchanged' : 'changed';
      } else if (current !== null) {
        status = 'added';
      } else {
        status = 'removed';
      }

      return { codeId, currentNote: current, previousNote: previous, status };
    });
  }

  search(): void {
    this.currentPage = 0;
    this.PopulateResults(0, this.pageSize);
  }

  public updateSearchInputString(event: { target: { value: string; }; }): void {
    this.searchInputString = event.target.value;
  }

  public searchInputKeyupEnter(event: { target: { value: string; }; }): void {
    this.searchInputString = event.target.value;
    this.currentPage = 0;
    this.search();
  }

  clearButton(): void {
    this.searchInputString = "";
    // Always maintain ID ascending sort (menor a mayor) even when clearing
    this.currentSorting = { active: 'ID', direction: 'asc' };
    const sortedData = this.sortResults([...this.commonNoteViewModels]);
    this.dataSource = sortedData;
    this.currentPage = 0;
    this.PopulateResults(0, this.pageSize);
  }

  getFilterResult() {
    const filteredResults = this.filterResults();

    const sortedResults: CommonNoteViewModel[] = this.sortResults(filteredResults);

    return sortedResults;
  }

  private sortResults(filterdResults: CommonNoteViewModel[]) {
    if (this.currentSorting) {
      const columName: string = this.currentSorting.active;
      const sortType = this.currentSorting.direction;

      // For ID column, always sort by codeId numerically
      if (columName === 'ID') {
        if (sortType === 'asc') {
          filterdResults.sort((a, b) => {
            const aVal = parseInt(a.codeId, 10);
            const bVal = parseInt(b.codeId, 10);
            return aVal - bVal;
          });
        } else if (sortType === 'desc') {
          filterdResults.sort((a, b) => {
            const aVal = parseInt(a.codeId, 10);
            const bVal = parseInt(b.codeId, 10);
            return bVal - aVal;
          });
        }
      } else {
        // For other columns, use the original logic with pascal casing
        const pascalCasing = columName.charAt(0).toLocaleLowerCase() + columName.substring(1, columName.length);

        if (sortType === 'asc') {
          filterdResults.sort((a, b) => {
            const aVal = a[pascalCasing];
            const bVal = b[pascalCasing];
            if (!isNaN(aVal)) {
              return this.isBigger(+aVal, +bVal);
            }
            return this.isBigger(aVal, bVal);
          });
        } else if (sortType === 'desc') {
          filterdResults.sort((a, b) => {
            const aVal = a[pascalCasing];
            const bVal = b[pascalCasing];
            if (!isNaN(aVal)) {
              return this.isSmaller(+aVal, +bVal);
            }
            return this.isSmaller(aVal, bVal);
          });
        }
      }
    }

    return filterdResults;
  }

  private isBigger(val1: number, val2: number): number{
    return val1 > val2 ? 1 : -1;
  }

  private isSmaller(val1: number, val2: number): number{
    return val1 < val2 ? 1 : -1;
  }

  private filterResults() {
    let filterResult = [];
    if (this.searchInputString && this.searchInputString.trim().length > 0) {
      filterResult = this.commonNoteViewModels.filter(
        (component) => component.codeId.toLowerCase().includes(this.searchInputString.toLocaleLowerCase()) ||
          component.note.toLowerCase().includes(this.searchInputString.toLocaleLowerCase())
      );
    } else {
      filterResult = this.commonNoteViewModels;
    }
    return filterResult;
  }

  PopulateResults(startIndex: number, endIndex: number) {
    const filterResult = this.getFilterResult();
    this.totalCount = filterResult.length;
    const pageItems = filterResult.slice(startIndex, endIndex);
    this.dataSource = pageItems;

    if (startIndex == 0 && this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  public updatePage(event: PageEvent) {
    const startIndex = event.pageIndex * event.pageSize;
    const endIndex = startIndex + event.pageSize;
    this.PopulateResults(startIndex, endIndex);
  }

  public sortColumn(event: Sort): void {
    this.currentSorting = event;
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    const endIndex = startIndex + this.paginator.pageSize;
    this.PopulateResults(startIndex, endIndex);

  }
}
